import { useState, useEffect, useRef, useCallback } from 'react'
import confetti from 'canvas-confetti'
import { chatTaskBreakdown } from '@/utils/decomposeTask'
import { playSubtaskCheck, playTaskComplete } from '@/utils/feedback'
import { useSpeechToText } from '@/hooks/useSpeechToText'
import useResizablePanel from '@/hooks/useResizablePanel'
import SwipeableRow from './SwipeableRow'
import ChatPanel from './ChatPanel'
import SparklesIcon from './icons/SparklesIcon'
import { isNativePlatform } from '@/utils/platform'

const PRIORITY_LABELS = { high: 'High', medium: 'Med', low: 'Low' }

function TaskCard({ task, autoExpand, onClearExpandTask, onToggleSubtask, onDeleteTask, onUpdateTask, onUpdateSubtask, onAddSubtask, onDeleteSubtask, onReorderSubtasks, onReplaceSubtasks, onStartFocus }) {
  const [expanded, setExpanded] = useState(false)
  const cardRef = useRef(null)
  const [dragIndex, setDragIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)

  useEffect(() => {
    if (autoExpand) {
      setExpanded(true)
      onClearExpandTask()
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
    }
  }, [autoExpand, onClearExpandTask])
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [editTime, setEditTime] = useState('')
  const [adding, setAdding] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newTime, setNewTime] = useState('15')

  const [editingDueDate, setEditingDueDate] = useState(false)

  // Chat state
  const [chatOpen, setChatOpen] = useState(false)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const { isListening, startListening, stopListening, isSupported: micSupported, interimText } = useSpeechToText(
    useCallback((text) => setChatInput((prev) => prev ? prev + ' ' + text : text), [])
  )
  const { chatWidth, containerRef, onPointerDown } = useResizablePanel()

  const doneCount = task.subtasks.filter((st) => st.done).length
  const total = task.subtasks.length
  const isComplete = total > 0 && doneCount === total
  const progress = total > 0 ? (doneCount / total) * 100 : 0
  const [showCheckmark, setShowCheckmark] = useState(false)
  const prevCompleteRef = useRef(isComplete)

  useEffect(() => {
    if (isComplete && !prevCompleteRef.current) {
      setShowCheckmark(true)
      playTaskComplete()
      // Fire confetti from the card position
      const rect = cardRef.current?.getBoundingClientRect()
      if (rect) {
        confetti({
          particleCount: 80,
          spread: 60,
          origin: {
            x: (rect.left + rect.width / 2) / window.innerWidth,
            y: (rect.top + rect.height / 2) / window.innerHeight,
          },
          zIndex: 9999,
          disableForReducedMotion: true,
        })
      }
      setTimeout(() => setShowCheckmark(false), 1200)
    }
    prevCompleteRef.current = isComplete
  }, [isComplete])
  const totalMinutes = task.subtasks.reduce((sum, st) => sum + st.timeEstimate, 0)
  const remainingMinutes = task.subtasks
    .filter((st) => !st.done)
    .reduce((sum, st) => sum + st.timeEstimate, 0)

  const now = new Date()
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const isOverdue = task.dueDate < today && !isComplete

  function formatDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  function formatTime(minutes) {
    if (minutes < 60) return `${minutes}m`
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return m > 0 ? `${h}h ${m}m` : `${h}h`
  }

  function handleDeleteTask(e) {
    e.stopPropagation()
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    onDeleteTask(task.id)
  }

  function handleCancelDelete(e) {
    e.stopPropagation()
    setConfirmDelete(false)
  }

  function startEdit(st, e) {
    e.stopPropagation()
    setEditingId(st.id)
    setEditTitle(st.title)
    setEditTime(String(st.timeEstimate))
  }

  function saveEdit(e) {
    e.preventDefault()
    if (!editTitle.trim()) return
    const time = Math.max(1, Math.min(999, parseInt(editTime, 10) || 15))
    onUpdateSubtask(task.id, editingId, { title: editTitle.trim(), timeEstimate: time })
    setEditingId(null)
  }

  function cancelEdit() {
    setEditingId(null)
  }

  function handleDeleteSubtask(subtaskId, e) {
    e.stopPropagation()
    onDeleteSubtask(task.id, subtaskId)
  }

  function handleAddSubtask(e) {
    e.preventDefault()
    if (!newTitle.trim()) return
    const time = Math.max(1, Math.min(999, parseInt(newTime, 10) || 15))
    const subtaskId = `${task.id}-${crypto.randomUUID().slice(0, 8)}`
    onAddSubtask(task.id, { id: subtaskId, title: newTitle.trim(), timeEstimate: time, done: false })
    setNewTitle('')
    setNewTime('15')
    setAdding(false)
  }

  function handleDragStart(index) {
    setDragIndex(index)
  }

  function handleDragOver(e, index) {
    e.preventDefault()
    if (dragIndex === null || index === dragIndex) return
    setDragOverIndex(index)
  }

  function handleDrop(index) {
    if (dragIndex !== null && dragIndex !== index) {
      onReorderSubtasks(task.id, dragIndex, index)
    }
    setDragIndex(null)
    setDragOverIndex(null)
  }

  function handleDragEnd() {
    setDragIndex(null)
    setDragOverIndex(null)
  }

  // Touch-based drag and drop for mobile
  function handleTouchStart(index, e) {
    e.preventDefault()
    setDragIndex(index)
  }

  function handleTouchMove(e) {
    if (dragIndex === null) return
    const touch = e.touches[0]
    const elem = document.elementFromPoint(touch.clientX, touch.clientY)
    if (!elem) return
    const li = elem.closest('.subtask-item')
    if (!li) return
    const list = li.parentElement
    if (!list) return
    const items = Array.from(list.querySelectorAll('.subtask-item'))
    const overIndex = items.indexOf(li)
    if (overIndex !== -1 && overIndex !== dragIndex) {
      setDragOverIndex(overIndex)
    }
  }

  function handleTouchEnd() {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      onReorderSubtasks(task.id, dragIndex, dragOverIndex)
    }
    setDragIndex(null)
    setDragOverIndex(null)
  }

  function handleOpenChat() {
    setChatOpen(true)
    setExpanded(true)
  }

  function handleCloseChat() {
    setChatOpen(false)
    setChatMessages([])
    setChatInput('')
  }

  async function handleChatSend() {
    const message = chatInput.trim()
    if (!message || chatLoading) return

    const userMsg = { role: 'user', text: message }
    const updatedMessages = [...chatMessages, userMsg]
    setChatMessages(updatedMessages)
    setChatInput('')
    setChatLoading(true)

    try {
      const result = await chatTaskBreakdown({
        userMessage: message,
        task,
        chatHistory: chatMessages,
      })

      const assistantMsg = { role: 'assistant', text: result.message }
      setChatMessages((prev) => [...prev, assistantMsg])

      if (result.type === 'subtasks' && Array.isArray(result.subtasks)) {
        onReplaceSubtasks(task.id, result.subtasks)
      }
    } catch (err) {
      setChatMessages((prev) => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }])
      console.error('Chat task error:', err.message)
    } finally {
      setChatLoading(false)
    }
  }

  const cardClassName = `task-card ${isComplete ? 'task-card--done' : ''} ${isOverdue ? 'task-card--overdue' : ''} ${chatOpen ? 'task-card--chat-open' : ''}`

  return (
    <div ref={containerRef} className={chatOpen ? 'task-card-chat-wrapper task-card-chat-wrapper--open' : ''}>
    <div ref={cardRef} className={cardClassName} style={{ position: 'relative' }}>
      <button className="task-card-header" onClick={() => {
        const willExpand = !expanded
        setExpanded(willExpand)
        if (willExpand) {
          setTimeout(() => {
            cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }, 50)
        }
      }}>
        <div className="task-card-top">
          <span className={`priority-badge priority-badge--${task.priority}`}>
            {PRIORITY_LABELS[task.priority]}
          </span>
          {editingDueDate ? (
            <span className="task-due-edit" onClick={(e) => e.stopPropagation()}>
              <input
                type="date"
                className="task-due-input"
                value={task.dueDate}
                onChange={(e) => {
                  onUpdateTask(task.id, { dueDate: e.target.value })
                  setEditingDueDate(false)
                }}
                onBlur={() => setEditingDueDate(false)}
                autoFocus
              />
            </span>
          ) : (
            <span
              className={`task-due ${isOverdue ? 'task-due--overdue' : ''}`}
              onClick={(e) => { e.stopPropagation(); setEditingDueDate(true) }}
              title="Click to change due date"
            >
              {isOverdue ? 'Overdue · ' : ''}Due {formatDate(task.dueDate)} ✎
            </span>
          )}
        </div>

        <h3 className="task-card-title">{task.title}</h3>

        <div className="task-card-meta">
          <span className="task-progress-text">
            {doneCount} of {total} steps done
          </span>
          <span className="task-time-left">
            {isComplete ? 'Complete!' : `${formatTime(remainingMinutes)} left`}
          </span>
        </div>

        <div className="progress-bar">
          <div
            className="progress-bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        <span className="task-card-expand">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="task-card-body">
          <div className="task-card-actions">
            <span className="task-total-time">Total: {formatTime(totalMinutes)}</span>
            <div className="task-card-action-buttons">
              {!isComplete && onStartFocus && (
                <button
                  className="btn btn-secondary btn-focus-task"
                  onClick={() => onStartFocus({ taskId: task.id, subtaskId: null })}
                >
                  ▶ Focus
                </button>
              )}
              {!chatOpen && (
                <button
                  className="btn-icon btn-chat-ai btn-chat-task"
                  onClick={handleOpenChat}
                  title="Chat with AI"
                  aria-label="Chat with AI"
                >
                  <SparklesIcon />
                </button>
              )}
              <button
                className="btn-icon btn-add-subtask"
                onClick={() => setAdding((prev) => !prev)}
                title="Add subtask"
              >
                +
              </button>
              {confirmDelete ? (
                <span className="delete-confirm">
                  <span className="delete-confirm-text">Delete task?</span>
                  <button className="btn-confirm-yes" onClick={handleDeleteTask}>Yes</button>
                  <button className="btn-confirm-no" onClick={handleCancelDelete}>No</button>
                </span>
              ) : (
                <button
                  className="btn-icon btn-delete-task"
                  onClick={handleDeleteTask}
                  title="Delete task"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {adding && (
            <form className="subtask-add-form" onSubmit={handleAddSubtask}>
              <input
                type="text"
                className="subtask-edit-input"
                placeholder="New subtask name"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                autoFocus
              />
              <input
                type="number"
                className="subtask-edit-time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                min="1"
                max="999"
              />
              <span className="subtask-edit-min-label">min</span>
              <button type="submit" className="btn-icon btn-save">✓</button>
              <button type="button" className="btn-icon btn-cancel" onClick={() => setAdding(false)}>✕</button>
            </form>
          )}

          <ul className="subtask-list">
            {task.subtasks.map((st, index) => (
              <li
                key={st.id}
                className={`subtask-item ${st.done ? 'subtask-item--done' : ''}${dragIndex === index ? ' subtask-item--dragging' : ''}${dragOverIndex === index ? ' subtask-item--drag-over' : ''}`}
                draggable={editingId !== st.id}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
              >
                {editingId === st.id ? (
                  <form className="subtask-edit-form" onSubmit={saveEdit}>
                    <input
                      type="checkbox"
                      className="subtask-checkbox"
                      checked={st.done}
                      onChange={() => onToggleSubtask(task.id, st.id)}
                    />
                    <input
                      type="text"
                      className="subtask-edit-input"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      autoFocus
                    />
                    <input
                      type="number"
                      className="subtask-edit-time"
                      value={editTime}
                      onChange={(e) => setEditTime(e.target.value)}
                      min="1"
                      max="999"
                    />
                    <span className="subtask-edit-min-label">min</span>
                    <button type="submit" className="btn-icon btn-save">✓</button>
                    <button type="button" className="btn-icon btn-cancel" onClick={cancelEdit}>✕</button>
                  </form>
                ) : (
                  <SwipeableRow
                    actions={[
                      ...(!st.done && onStartFocus && isNativePlatform() ? [{ key: 'focus', icon: '▶', label: 'Focus', onClick: () => onStartFocus({ taskId: task.id, subtaskId: st.id }), variant: 'focus' }] : []),
                      { key: 'edit', icon: '✎', label: 'Edit', onClick: (e) => startEdit(st, e), variant: 'edit' },
                      { key: 'delete', icon: '✕', label: 'Delete', onClick: (e) => handleDeleteSubtask(st.id, e), variant: 'delete' },
                    ]}
                    disabled={editingId !== null}
                  >
                    <label className="subtask-label">
                      <span
                        className="subtask-drag-handle"
                        title="Drag to reorder"
                        onTouchStart={(e) => handleTouchStart(index, e)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                      >⠿</span>
                      <input
                        type="checkbox"
                        className="subtask-checkbox"
                        checked={st.done}
                        onChange={(e) => {
                          playSubtaskCheck()
                          e.target.classList.add('checkbox-pop')
                          setTimeout(() => e.target.classList.remove('checkbox-pop'), 250)
                          onToggleSubtask(task.id, st.id)
                        }}
                      />
                      <span className="subtask-title">{st.title}</span>
                      <span className="subtask-time">{formatTime(st.timeEstimate)}</span>
                      {!st.done && onStartFocus && !isNativePlatform() && (
                        <button
                          className="btn-icon btn-focus-subtask"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onStartFocus({ taskId: task.id, subtaskId: st.id }) }}
                          title="Focus on this step"
                        >
                          ▶
                        </button>
                      )}
                      <button
                        className="btn-icon btn-edit-subtask"
                        onClick={(e) => startEdit(st, e)}
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button
                        className="btn-icon btn-delete-subtask"
                        onClick={(e) => handleDeleteSubtask(st.id, e)}
                        title="Delete"
                      >
                        ✕
                      </button>
                    </label>
                  </SwipeableRow>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showCheckmark && (
        <div className="task-complete-overlay">
          <svg viewBox="0 0 52 52">
            <circle className="checkmark-circle" cx="26" cy="26" r="24" />
            <path className="checkmark-check" d="M14 27l8 8 16-16" />
          </svg>
        </div>
      )}

    </div>

      {chatOpen && (
        <ChatPanel
          title="Chat with AI about this breakdown"
          placeholder="Describe changes you'd like to the task breakdown"
          messages={chatMessages}
          input={chatInput}
          onInputChange={setChatInput}
          onSend={handleChatSend}
          loading={chatLoading}
          onClose={handleCloseChat}
          chatWidth={chatWidth}
          onPointerDown={onPointerDown}
          isListening={isListening}
          startListening={startListening}
          stopListening={stopListening}
          micSupported={micSupported}
          interimText={interimText}
        />
      )}
    </div>
  )
}

export default TaskCard
